/*
 * Copyright 2025 ixPilot Teknoloji A.Ş.
 * All rights reserved.
 *
 * Shared hook to load firm options from the backend once and expose
 * loading / error state alongside a manual refresh helper.
 */

import { useCallback, useEffect, useState } from "react";
import api from "utils/api";

/** Araçlar / Hesaplar / Telemetri / Abonelik — firma seçici (hafif API) */
const FIRMA_ENDPOINT = "/api/firmalar?select_only=true";

/** Sayfalar arası geçişte aynı listeyi tekrar çekmemek için (oturum içi) */
let sharedFirmsCache = null;
let sharedFirmsLoadPromise = null;

const normalizeFirm = (firma) => {
  if (!firma || typeof firma !== "object") return null;

  const id =
    firma.id ??
    firma.HspId ??
    firma.firmaId ??
    firma.firma_id ??
    firma.firm_id ??
    null;

  const name =
    firma.firma_ismi ??
    firma.firma ??
    firma.name ??
    firma.firmaName ??
    null;

  if (!id || !name) return null;

  return {
    id: String(id),
    name,
    raw: firma,
  };
};

export const useFirmOptions = () => {
  const [firms, setFirms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const applyFirmList = useCallback((data) => {
    if (Array.isArray(data)) {
      const normalized = data
        .map(normalizeFirm)
        .filter(Boolean)
        .sort((a, b) => a.name.localeCompare(b.name, "tr"));
      sharedFirmsCache = normalized;
      setFirms(normalized);
    } else {
      sharedFirmsCache = [];
      setFirms([]);
    }
  }, []);

  const loadFirms = useCallback(
    (force = false) => {
      if (!force && sharedFirmsCache) {
        setFirms(sharedFirmsCache);
        setLoading(false);
        setError(null);
        return;
      }

      if (!force && sharedFirmsLoadPromise) {
        setLoading(true);
        sharedFirmsLoadPromise
          .then(applyFirmList)
          .catch(() => {
            setError("Firma listesi yüklenemedi. Lütfen tekrar deneyin.");
            setFirms([]);
          })
          .finally(() => setLoading(false));
        return;
      }

      setLoading(true);
      setError(null);

      sharedFirmsLoadPromise = api
        .get(FIRMA_ENDPOINT)
        .then((res) => res.json())
        .then((data) => {
          applyFirmList(data);
          return data;
        })
        .catch((err) => {
          sharedFirmsCache = null;
          setError("Firma listesi yüklenemedi. Lütfen tekrar deneyin.");
          setFirms([]);
          throw err;
        })
        .finally(() => {
          setLoading(false);
          sharedFirmsLoadPromise = null;
        });
    },
    [applyFirmList]
  );

  useEffect(() => {
    loadFirms();
  }, [loadFirms]);

  const reload = useCallback(() => {
    sharedFirmsCache = null;
    sharedFirmsLoadPromise = null;
    loadFirms(true);
  }, [loadFirms]);

  return {
    firms,
    loading,
    error,
    reload,
  };
};

export default useFirmOptions;

